import { useEffect, useMemo } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, TrashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { createInvoice } from '../services/invoiceService';
import { getClients } from '../services/clientService';

const itemSchema = z.object({
  description: z.string().min(1, 'Required'),
  quantity: z.coerce.number().int().min(1),
  unitPrice: z.coerce.number().min(0),
});

const schema = z.object({
  clientId: z.string().uuid('Select a client'),
  invoiceDate: z.string().min(1, 'Required'),
  dueDate: z.string().min(1, 'Required'),
  items: z.array(itemSchema).min(1, 'Add at least one item'),
  tax: z.coerce.number().min(0).default(0),
  discount: z.coerce.number().min(0).default(0),
  notes: z.string().optional().or(z.literal('')),
});

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error.message}</p>}
  </div>
);

const today = format(new Date(), 'yyyy-MM-dd');
const thirtyDaysOut = format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');

export default function InvoiceForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const prefilledClientId = searchParams.get('clientId') ?? '';

  const { data: clientsData } = useQuery({
    queryKey: ['clients', { limit: 200 }],
    queryFn: () => getClients({ limit: 200 }),
  });
  const clients = useMemo(() => clientsData?.clients ?? [], [clientsData]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      clientId: prefilledClientId,
      invoiceDate: today,
      dueDate: thirtyDaysOut,
      items: [{ description: '', quantity: 1, unitPrice: '' }],
      tax: 0,
      discount: 0,
      notes: '',
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  // Re-sync client select after async data loads
  useEffect(() => {
    if (prefilledClientId && clients.length > 0) {
      setValue('clientId', prefilledClientId);
    }
  }, [clients, prefilledClientId, setValue]);

  // Live totals calculation
  const watchedItems = useWatch({ control, name: 'items' });
  const watchedTax = useWatch({ control, name: 'tax' });
  const watchedDiscount = useWatch({ control, name: 'discount' });

  const subtotal = (watchedItems || []).reduce(
    (sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0),
    0
  );
  const total = subtotal + (parseFloat(watchedTax) || 0) - (parseFloat(watchedDiscount) || 0);

  const mutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: ({ invoice }) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoiceSummary'] });
      toast.success(`Invoice ${invoice.invoiceNumber} created`);
      navigate(`/billing/${invoice.id}`);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create invoice'),
  });

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/billing" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">New Invoice</h1>
      </div>

      <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-6">
        {/* Header */}
        <div className="card">
          <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Client *" error={errors.clientId}>
              <select className="input" {...register('clientId')}>
                <option value="">Select a client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.lastName}, {c.firstName}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Invoice date *" error={errors.invoiceDate}>
              <input type="date" className="input" {...register('invoiceDate')} />
            </Field>
            <Field label="Due date *" error={errors.dueDate}>
              <input type="date" className="input" {...register('dueDate')} />
            </Field>
          </div>
        </div>

        {/* Line items */}
        <div className="card">
          <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Line Items</h2>
          {errors.items?.root && (
            <p className="text-sm text-danger-600 mb-3">{errors.items.root.message}</p>
          )}

          <div className="overflow-x-auto">
            <div className="space-y-3 min-w-[500px]">
            {/* Header row */}
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide px-1">
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-center">Qty</div>
              <div className="col-span-2 text-right">Unit price</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-1" />
            </div>

            {fields.map((field, index) => {
              const qty = parseFloat(watchedItems?.[index]?.quantity) || 0;
              const price = parseFloat(watchedItems?.[index]?.unitPrice) || 0;
              return (
                <div key={field.id} className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-6">
                    <input
                      type="text"
                      className="input"
                      placeholder="Description…"
                      {...register(`items.${index}.description`)}
                    />
                    {errors.items?.[index]?.description && (
                      <p className="mt-1 text-xs text-danger-600">{errors.items[index].description.message}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      className="input text-center"
                      min={1}
                      {...register(`items.${index}.quantity`)}
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      className="input text-right"
                      min={0}
                      step={0.01}
                      placeholder="0.00"
                      {...register(`items.${index}.unitPrice`)}
                    />
                  </div>
                  <div className="col-span-1 flex items-center justify-end pt-2 text-sm text-neutral-700 dark:text-neutral-300 font-medium">
                    {fmt(qty * price)}
                  </div>
                  <div className="col-span-1 flex items-center justify-center pt-1">
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                      className="text-neutral-400 dark:text-neutral-500 hover:text-danger-500 disabled:opacity-30 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          </div>

          <button
            type="button"
            onClick={() => append({ description: '', quantity: 1, unitPrice: '' })}
            className="mt-4 flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add line item
          </button>
        </div>

        {/* Totals + Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="card">
            <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Notes</h2>
            <textarea
              className="input"
              rows={4}
              placeholder="Payment terms, instructions…"
              {...register('notes')}
            />
          </div>

          <div className="card">
            <h2 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-4">Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Subtotal</span>
                <span className="font-medium">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Tax ($)</span>
                <input
                  type="number"
                  className="input w-28 text-right text-sm"
                  min={0}
                  step={0.01}
                  {...register('tax')}
                />
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">Discount ($)</span>
                <input
                  type="number"
                  className="input w-28 text-right text-sm"
                  min={0}
                  step={0.01}
                  {...register('discount')}
                />
              </div>
              <div className="flex justify-between text-base font-bold border-t border-neutral-200 dark:border-neutral-700 pt-3">
                <span>Total</span>
                <span>{fmt(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Link to="/billing" className="btn btn-secondary">Cancel</Link>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {mutation.isPending ? 'Creating…' : 'Create invoice'}
          </button>
        </div>
      </form>
    </div>
  );
}
